import { ApolloClient, ApolloQueryResult } from '@apollo/client';
import { getCache, getCacheKey, setCache } from 'lib/cache';
import { extractContent, validateLanguage } from 'lib/globalization/language';
import { logError, logInfo } from 'lib/utilities/logger';
import { BrandCode } from 'types';
import {
	CruiseDocument,
	CruiseFilter,
	CruiseGroupListFilter,
	CruiseListFilter,
	CruiseQuery,
	CruiseQueryVariables,
	CruiseSearch_CruisesDocument,
	CruiseSearch_CruisesQuery,
	CruiseSearch_CruisesQueryVariables,
	CruiseSearch_FiltersFacetsDocument,
	CruiseSearch_FiltersFacetsQuery,
	CruiseSearch_FiltersFacetsQueryVariables,
	CruiseSearch_FiltersOptionsDocument,
	CruiseSearch_FiltersOptionsQuery,
	GetSailingsByCruiseIdDocument,
	GetSailingsByCruiseIdQuery,
	GetSailingsByCruiseIdQueryVariables,
} from '../codegen/cruises/graphql';
import { GraphServer } from '../constants';
import { executeQuery, executeQueryIgnoreError } from '../utils';

export * from '../codegen/cruises';
export * from '../codegen/cruises/graphql';

export function extractFilterOptionsContent(fitlerOptions: Array<CruiseFilter>) {
	const content = [];
	fitlerOptions.forEach((item) => {
		if (item.key === 'destination' || item.key === 'port') {
			const list: CruiseListFilter = item;
			list.options
				.filter((option) => option.label && option.label !== 'MISSING PORT CONTENT')
				.forEach((option) => {
					content.push(option.label);
				});
		}
		if (item.key === 'departurePort') {
			const group: CruiseGroupListFilter = item;
			group.groups?.forEach((groupItem) => {
				content.push(groupItem.label ?? '');
				groupItem.options?.forEach((option) => {
					content.push(option.label ?? '');
				});
			});
		}
	});
	return extractContent(content);
}

export const getFacetOptions = async <TCacheShape>(
	apolloClient: ApolloClient<TCacheShape>,
	variables: CruiseSearch_FiltersFacetsQueryVariables & { currency?: string },
): Promise<ApolloQueryResult<CruiseSearch_FiltersFacetsQuery>> => {
	const { currency, ...otherVariables } = variables;
	return executeQuery(apolloClient, GraphServer.CRUISE_SEARCH, CruiseSearch_FiltersFacetsDocument, otherVariables, {
		headers: {
			currency,
		},
	});
};

export const getFilterOptions = async <TCacheShape>(
	apolloClient: ApolloClient<TCacheShape>,
): Promise<ApolloQueryResult<CruiseSearch_FiltersOptionsQuery>> => {
	return executeQueryIgnoreError(apolloClient, GraphServer.CRUISE_SEARCH, CruiseSearch_FiltersOptionsDocument);
};

export const getFilterOptionsFromSource = async <TCacheShape>(
	apolloClient: ApolloClient<TCacheShape>,
	variables: {
		brand: BrandCode;
		country: string;
		language: string;
		currency: string;
		office: string;
		logId?: string;
	},
): Promise<Array<CruiseFilter>> => {
	const { brand, country, language, currency, office, logId } = variables;
	try {
		const cacheKey = getCacheKey('filter_options', [brand, country, office, language, currency]);
		const cachedData = getCache<Array<CruiseFilter>>(cacheKey);

		if (cachedData) {
			logInfo('[Server Cache] Filter Options Cache Hit', { cacheKey, logId });
			return cachedData;
		}

		logInfo('[Server Cache] Filter Options Cache Miss', { cacheKey, logId });
		const filters = await getFilterOptions(apolloClient);
		const { data, error } = filters;
		const filterOptions = data?.cruiseSearch?.filters || [];

		if (error) {
			logError('getFilterOptions error', { error, ...variables });
		}
		if (filterOptions.length > 0 && validateLanguage(language, extractFilterOptionsContent(filterOptions), logId)) {
			setCache(cacheKey, filterOptions);
		}

		return filterOptions;
	} catch (error) {
		logError('getFilterOptionsFromSource error', { error, ...variables });
		return [];
	}
};

export const getCruiseById = async <TCacheShape>(
	apolloClient: ApolloClient<TCacheShape>,
	variables: CruiseQueryVariables,
): Promise<ApolloQueryResult<CruiseQuery>> => {
	return executeQuery(apolloClient, GraphServer.CRUISE_SEARCH, CruiseDocument, variables);
};

export const getFilterResults = async <TCacheShape>(
	apolloClient: ApolloClient<TCacheShape>,
	variables: CruiseSearch_CruisesQueryVariables & { currency?: string },
): Promise<ApolloQueryResult<CruiseSearch_CruisesQuery>> => {
	const { currency, ...otherVariables } = variables;
	return executeQueryIgnoreError(apolloClient, GraphServer.CRUISE_SEARCH, CruiseSearch_CruisesDocument, otherVariables, {
		headers: {
			currency,
		},
	});
};

export const getSailingsByCruise = async <TCacheShape>(
	apolloClient: ApolloClient<TCacheShape>,
	variables: GetSailingsByCruiseIdQueryVariables,
): Promise<ApolloQueryResult<GetSailingsByCruiseIdQuery>> => {
	return executeQuery(apolloClient, GraphServer.CRUISE_SEARCH, GetSailingsByCruiseIdDocument, variables);
};
